import type { ObjectRecord } from '@vp/shared-types';
import { compareSemver, parseSemver } from '@vp/shared-types';
import { CORE_FORMAT_VERSION } from './objects';

/** Data format version (independent from the module API version, §8.6). */
export const DATA_FORMAT_VERSION = '1.0.0';

export class FormatTooNewError extends Error {
  readonly code = 'E_FORMAT_TOO_NEW';
  constructor(readonly found: string) {
    super(`Data format ${found} is newer than supported ${DATA_FORMAT_VERSION}`);
  }
}

/** Is a stored/imported data format readable by this build? Newer major → no; newer minor → readable (additive). */
export function checkFormat(found: string): 'ok' | 'too-new' | 'invalid' {
  const v = parseSemver(found);
  const cur = parseSemver(DATA_FORMAT_VERSION)!;
  if (!v) return 'invalid';
  if (v.major > cur.major) return 'too-new';
  if (v.major === cur.major && compareSemver(v, cur) > 0 && v.minor > cur.minor) return 'ok';
  return 'ok';
}

type CoreMigration = (rec: ObjectRecord) => ObjectRecord;

/** Core object migrations: index = from version (§8.5). Empty until the first format change. */
const coreMigrations: Record<number, CoreMigration> = {};

/**
 * Migrate a core object to the current format. The input is never mutated, so the
 * caller keeps the original until the migrated record is safely stored.
 */
export function migrateCoreObject(rec: ObjectRecord): ObjectRecord {
  let cur = rec;
  let v = rec.formatVersion;
  while (v < CORE_FORMAT_VERSION) {
    const m = coreMigrations[v];
    cur = m ? m(structuredClone(cur)) : cur;
    v++;
    cur = { ...cur, formatVersion: v };
  }
  return cur;
}
